import { useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useDismissable } from "./useDismissable";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Footer actions (right-aligned). */
  footer?: ReactNode;
  /** Set false to require an explicit action (no overlay-click / Esc dismissal). */
  dismissable?: boolean;
}

/**
 * Modal — focus-trapped dialog (role="dialog", aria-modal), Esc + overlay-click to close, scroll
 * lock, focus restore. Rendered through a portal at the overlay z-layer.
 */
export function Modal({ open, onClose, title, children, footer, dismissable = true }: ModalProps) {
  const titleId = useId();
  const panelRef = useDismissable(open, dismissable ? onClose : () => {});
  if (!open) return null;

  return createPortal(
    <div
      className="nv-overlay"
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="nv-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="nv-modal__header">
          <h2 className="nv-modal__title" id={titleId}>
            {title}
          </h2>
          {dismissable && (
            <button type="button" className="nv-iconbtn" aria-label="Fermer" onClick={onClose}>
              ×
            </button>
          )}
        </header>
        <div className="nv-modal__body">{children}</div>
        {footer && <footer className="nv-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
